import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";

// Ctrl+R from anywhere: find an item and change its sale rate.
export const SalesRateDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setPicked(null);
    setRate("");
    api.get("/items").then(({ data }) => setItems(data)).catch(() => {});
  }, [open]);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t
      ? items.filter((i) => [i.name, i.company, i.quality].some((v) => String(v || "").toLowerCase().includes(t)))
      : items;
    return list.slice(0, 40);
  }, [items, q]);

  const save = async () => {
    if (!picked) return;
    const value = parseFloat(rate);
    if (Number.isNaN(value)) return toast.error("Type a sale rate");
    setSaving(true);
    try {
      await api.patch(`/items/${picked.id}/sales-price`, { sales_price: value });
      setItems((ls) => ls.map((i) => (i.id === picked.id ? { ...i, sales_price: value } : i)));
      toast.success(`${picked.name} sale rate is now ₹${value}`);
      setPicked(null);
      setRate("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sales-rate-dialog" className="max-w-xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold tracking-tight">Update sale rate</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Search an item, type the new sale rate and save. Works from any screen (Ctrl+R).
          </DialogDescription>
        </DialogHeader>

        <Input
          autoFocus
          data-testid="sales-rate-search"
          className="rounded-sm"
          placeholder="Search item, company or quality"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPicked(null);
          }}
        />

        {picked ? (
          <div className="flex items-end gap-3 border border-[var(--mod-border)] bg-[var(--mod-tint)] p-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold uppercase">{picked.name}</p>
              <p className="text-xs text-zinc-500">
                {picked.company || "—"} · {picked.quality || "—"} · now ₹{picked.sales_price ?? "—"}
              </p>
            </div>
            <Input
              autoFocus
              data-testid="sales-rate-value"
              className="w-28 rounded-sm text-right"
              placeholder="New rate"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
            />
            <Button
              data-testid="sales-rate-save"
              disabled={saving}
              onClick={save}
              className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        ) : (
          <div className="max-h-[320px] overflow-y-auto border border-zinc-200">
            {rows.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-zinc-500">No item matches that search.</p>
            ) : (
              rows.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  data-testid={`sales-rate-pick-${i.id}`}
                  onClick={() => {
                    setPicked(i);
                    setRate(i.sales_price ?? "");
                  }}
                  className="flex w-full items-center justify-between gap-3 border-b border-zinc-100 px-3 py-2 text-left text-sm hover:bg-[var(--mod-hover)]"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-semibold uppercase">{i.name}</span>
                    <span className="text-xs text-zinc-500">
                      {i.company || "—"} · {i.quality || "—"}
                    </span>
                  </span>
                  <span className="font-mono-num shrink-0 font-semibold">
                    {i.sales_price === null || i.sales_price === undefined ? "—" : `₹${i.sales_price}`}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
